/** Dressed runner stones: stacked elliptical drums, radial furrows and one exposed central socket. */
import { checkEnvelope, checkScope, el, n, partGroup } from '../../svg';
import type { PieceArt, PieceRecipe, ResolvedPalette } from '../../contract';
import { amount, grainRng, ground, line } from './machinerySvg';
export interface MillstonesParams { readonly stones: number; readonly lean: number }
export type MillstonesRecipe = PieceRecipe<'mill.millstone_stack.v1', MillstonesParams>;
export const MILLSTONES_FRAME = { w: 110, h: 90 } as const;
export const MILL_MILLSTONES: MillstonesRecipe = {
  schema: 'hush.asset-recipe.v1', id: 'mill.millstone_stack.default.v1', generatorId: 'mill.millstone_stack.v1',
  seed: 20071007, paletteId: 'millrace_amber', params: { stones: 3, lean: 0.3 },
};
export function buildMillstones(recipe: MillstonesRecipe, p: ResolvedPalette, scope: string): PieceArt {
  checkEnvelope(recipe, MILL_MILLSTONES.generatorId); checkScope(scope);
  const stones = amount(recipe.params.stones, 'stones', 1, 3, true), lean = amount(recipe.params.lean, 'lean', -1, 1);
  const random = grainRng(recipe), parts: string[] = [];
  let drums = '', topX = 55, topY = 64;
  for (let i = 0; i < stones; i++) {
    const x = 55 + i * lean * 3, y = 64 - i * 22, radius = 43 - i * 2;
    topX = x; topY = y;
    drums += el('path', { d: `M${n(x - radius)} ${y}V${y + 14}A${radius} 11 0 0 0 ${n(x + radius)} ${y + 14}V${y}Z`, fill: p.stoneShadow, stroke: p.outline, 'stroke-width': 1.3 });
    for (let j = 0; j < 9; j++) {
      const sx = x - radius + 7 + j * (radius * 2 - 14) / 8;
      drums += line(sx, y + 8, sx + (random(`edge.${i}.${j}`) - 0.5) * 3, y + 17, p.stoneBase, 1.3);
    }
    drums += el('ellipse', { cx: x, cy: y, rx: radius, ry: 12, fill: p.stoneBase, stroke: p.outline, 'stroke-width': 1.3 })
      + el('ellipse', { cx: x, cy: y - 0.5, rx: radius - 3, ry: 9, fill: 'none', stroke: p.stoneLight, 'stroke-width': 1.1 });
    for (let j = 0; j < 12; j++) {
      const a = j * Math.PI / 6 + random(`furrow.${i}.${j}`) * 0.07;
      drums += line(x + Math.cos(a) * 10, y + Math.sin(a) * 2.5, x + Math.cos(a + 0.12) * (radius - 5), y + Math.sin(a + 0.12) * 8, p.stoneShadow, 0.9);
    }
  }
  drums += el('ellipse', { cx: topX, cy: topY, rx: 7, ry: 3.5, fill: p.outline })
    + line(topX - 4, topY + 2, topX + 4, topY + 2, p.stoneLight, 0.7);
  const body = partGroup(parts, 'millstone-ground', ground(110, 90, p)) + partGroup(parts, 'millstone-dressed-drums', drums);
  return { frame: MILLSTONES_FRAME, body, anchors: { support: { x: 55, y: 90 } }, parts,
    openings: [{ x: topX - 7, y: topY - 3.5, width: 14, height: 7, fill: p.outline }] };
}
